"use client";

import { useMemo, useRef, useState } from "react";
import Link from "next/link";
import { Star, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { EnquiryModal } from "../layout/EnquiryModal";
import { trackEvent } from "@/utils/gtm";
import { jobOpenings } from "@/data/jobOpenings";
import JobPostingWidget from "@/components/Widgets/JobPostingWidget";

export function JobOpenings() {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [activeDir, setActiveDir] = useState<"left" | "right" | null>("right");
    const [isEnquiryModalOpen, setIsEnquiryModalOpen] = useState(false);

    const scroll = (direction: "left" | "right") => {
        setActiveDir(direction);
        if (scrollContainerRef.current) {
            const scrollAmount = 400; // Adjust as needed
            if (direction === "left") {
                scrollContainerRef.current.scrollBy({ left: -scrollAmount, behavior: "smooth" });
            } else {
                scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
            }
        }
    };

    const jobsWithDates = useMemo(() => {
        const datePosted = new Date().toISOString().split("T")[0];
        const validThroughDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0];

        return jobOpenings.map((job) => ({
            ...job,
            datePosted,
            validThrough: validThroughDate,
            hiringOrganization: {
                name: "Seasoft Classes",
                sameAs: "https://seasoftclasses.com",
            },
        }));
    }, []);

    const jobs = jobOpenings;

    return (
        <section className="py-20 bg-[#F4F7FF] overflow-hidden">
            <div className="max-w-[1440px] mx-auto px-4 md:px-8 lg:px-12">
                <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-16">
                    {/* Left Content */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="w-full lg:w-[35%] text-left"
                    >
                        <span className="text-[#1B61FF] font-semibold text-sm mb-4 block">
                            2K+ Job Openings across
                        </span>
                        <h2 className="text-[#1A1A1A] text-3xl md:text-4xl font-bold mb-6 leading-[1.1]">
                            Current Job<br />Openings
                        </h2>
                        <p className="text-[#666666] text-sm md:text-[15px] leading-relaxed max-w-sm">
                            Discover open positions with our clients and find career opportunities aligned with your training and expertise.
                        </p>
                    </motion.div>

                    {/* Right Content - Job Cards Slider */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="w-full lg:w-[65%]"
                    >
                        <div
                            ref={scrollContainerRef}
                            className="flex gap-6 overflow-x-auto no-scrollbar scroll-smooth pb-8"
                        >
                            {jobs.map((job, idx) => (
                                <motion.div
                                    key={idx}
                                    whileHover={{ y: -5 }}
                                    className="min-w-[320px] md:min-w-[380px] bg-white p-10 rounded-2xl shadow-sm hover:shadow-xl transition-all flex flex-col"
                                >
                                    <div className="flex items-start gap-4 mb-6">
                                        <div className="w-16 h-16 bg-[#737CFB] rounded-xl flex items-center justify-center shrink-0">
                                            <Star className="text-white fill-white" size={24} />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-[#1A1A1A] text-lg leading-tight mb-1">
                                                {job.title}
                                            </h3>
                                            <p className="text-[#666666] text-sm">{job.locationLabel}</p>
                                        </div>
                                    </div>

                                    <p className="text-[#666666] text-[14px] leading-relaxed mb-10">
                                        {job.shortDescription}
                                    </p>

                                    <div className="flex items-center justify-between mt-auto">
                                        <button
                                            onClick={() => {
                                                trackEvent("Jobs", "apply_click", job.title);
                                                setIsEnquiryModalOpen(true);
                                            }}
                                            className="text-[#1B61FF] font-bold text-[15px] flex items-center gap-2 hover:gap-3 transition-all cursor-pointer bg-transparent border-none"
                                        >
                                            Apply Now <ArrowRight size={18} />
                                        </button>
                                        <span className="text-[#999999] text-[13px]">
                                            {job.applied}
                                        </span>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-2">
                            <Link
                                href="/jobs"
                                className="inline-flex items-center text-sm font-bold text-[#1B61FF] hover:underline"
                            >
                                View all job openings
                            </Link>
                        </div>

                        {/* Navigation Arrows */}
                        <div className="flex justify-end gap-3 mt-4">
                            <button
                                onClick={() => scroll("left")}
                                className={`w-12 h-12 rounded-full flex items-center justify-center border-none transform hover:scale-110 transition-all cursor-pointer shadow-md ${activeDir === "left"
                                    ? "bg-[#1B61FF] text-white"
                                    : "bg-white text-[#1B61FF]"
                                    }`}
                            >
                                <ChevronLeft size={24} />
                            </button>
                            <button
                                onClick={() => scroll("right")}
                                className={`w-12 h-12 rounded-full flex items-center justify-center border-none transform hover:scale-110 transition-all cursor-pointer shadow-md ${activeDir === "right" || activeDir === null
                                    ? "bg-[#1B61FF] text-white"
                                    : "bg-white text-[#1B61FF]"
                                    }`}
                            >
                                <ChevronRight size={24} />
                            </button>
                        </div>
                    </motion.div>
                </div>

                <EnquiryModal
                    isOpen={isEnquiryModalOpen}
                    onClose={() => setIsEnquiryModalOpen(false)}
                />

                <JobPostingWidget jobs={jobsWithDates as any} singleScriptArray={true} />
            </div>
        </section>
    );
}

